import React from 'react';
import logger from '../utils/logger';

/**
 * ErrorBoundary — catches render errors in any child component tree.
 *
 * Must be a class component; hooks cannot intercept render-phase errors.
 * Catches:
 *   • Runtime errors inside render(), lifecycle methods, or constructors
 *   • Errors thrown when a React.lazy() chunk fails to load (network error,
 *     deploy race condition, etc.)
 *
 * Does NOT catch:
 *   • Errors in event handlers (use try/catch there)
 *   • Async errors outside React's render cycle
 *   • Errors in the ErrorBoundary itself
 *
 * TODO: replace console.error with Sentry.captureException() once Sentry
 * is integrated (audit item M-9).
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
        this.handleReset = this.handleReset.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        logger.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
        // ChunkLoadError means webpack rebuilt and old chunk URLs are stale — a single reload always fixes it.
        if (error?.name === 'ChunkLoadError' && !sessionStorage.getItem('chunkReloaded')) {
            sessionStorage.setItem('chunkReloaded', '1');
            window.location.reload();
        }
    }

    handleReset() {
        this.setState({ hasError: false, error: null });
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }
            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <h2 style={styles.title}>Something went wrong</h2>
                        <p style={styles.message}>
                            An unexpected error occurred. You can try refreshing the page.
                        </p>
                        {process.env.NODE_ENV !== 'production' && this.state.error && (
                            <pre style={styles.detail}>
                                {this.state.error.toString()}
                            </pre>
                        )}
                        <div style={styles.actions}>
                            <button style={styles.primaryBtn} onClick={() => window.location.reload()}>
                                Refresh page
                            </button>
                            <button style={styles.secondaryBtn} onClick={this.handleReset}>
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '24px',
    },
    card: {
        maxWidth: '480px',
        width: '100%',
        padding: '32px',
        borderRadius: '8px',
        background: '#fff',
        boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
        textAlign: 'center',
    },
    title: {
        margin: '0 0 12px',
        fontSize: '20px',
        fontWeight: 600,
        color: '#1a1a2e',
    },
    message: {
        margin: '0 0 24px',
        fontSize: '14px',
        color: '#5a5a72',
        lineHeight: 1.6,
    },
    detail: {
        margin: '0 0 24px',
        padding: '12px',
        borderRadius: '4px',
        background: '#f5f5f5',
        fontSize: '12px',
        color: '#c0392b',
        textAlign: 'left',
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    actions: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
    },
    primaryBtn: {
        padding: '10px 24px',
        borderRadius: '6px',
        border: 'none',
        background: '#3f51b5',
        color: '#fff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    secondaryBtn: {
        padding: '10px 24px',
        borderRadius: '6px',
        border: '1px solid #3f51b5',
        background: '#fff',
        color: '#3f51b5',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
};

export default ErrorBoundary;
